import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { setSentryUser, Sentry } from "@/lib/sentry";
import { identifyUser, resetPostHog } from "@/lib/posthog";
import { queryClient } from "@/lib/query-client";
import { getBrowserFingerprint, getOrCreateDeviceId } from "@/lib/session-fingerprint";
import type { User } from "@supabase/supabase-js";

interface AalData {
  currentLevel: string;
  nextLevel: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  companyId: string | null;
  aal: AalData | null;
  connectionError: boolean;
  signOut: () => Promise<void>;
  retryConnection: () => void;
}

const AuthContext = createContext<AuthContextType>({ user: null, loading: true, companyId: null, aal: null, connectionError: false, signOut: async () => {}, retryConnection: () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [aal, setAal] = useState<AalData | null>(null);
  const [connectionError, setConnectionError] = useState(false);
  const mountedRef = useRef(true);
  const initializedRef = useRef(false);

  const fetchCompany = async (userId: string): Promise<string | null> => {
    // Hit owner + role membership in parallel. Owner wins if both return; the
    // sequential 3s-race version was firing the timeout on a healthy query
    // and cascading into a slower fallback path, leaving /dash stuck on a
    // loading shell for 6+ seconds.
    let resolved: string | null = null;
    try {
      const [ownerRes, memberRes] = await Promise.allSettled([
        supabase.from('companies').select('id').eq('owner_user_id', userId).limit(1).maybeSingle(),
        supabase.from('company_roles').select('company_id').eq('user_id', userId).not('accepted_at', 'is', null).limit(1).maybeSingle(),
      ]);
      const ownerId = ownerRes.status === 'fulfilled' ? ownerRes.value.data?.id ?? null : null;
      const memberId = memberRes.status === 'fulfilled' ? memberRes.value.data?.company_id ?? null : null;
      resolved = ownerId ?? memberId ?? null;
    } catch (e: unknown) {
      console.error('fetchCompany error:', e);
      resolved = null;
    }
    if (mountedRef.current) setCompanyId(resolved);
    return resolved;
  };

  // Highest-privilege role for analytics cohorts (admin > moderator > user).
  // Uses the has_role RPC so RLS on user_roles stays untouched.
  const fetchHighestRole = async (userId: string): Promise<"admin" | "moderator" | "user"> => {
    try {
      const [adminRes, modRes] = await Promise.allSettled([
        supabase.rpc('has_role', { _user_id: userId, _role: 'admin' }),
        supabase.rpc('has_role', { _user_id: userId, _role: 'moderator' }),
      ]);
      if (adminRes.status === 'fulfilled' && adminRes.value.data === true) return 'admin';
      if (modRes.status === 'fulfilled' && modRes.value.data === true) return 'moderator';
      return 'user';
    } catch {
      return 'user';
    }
  };

  const fetchAal = async () => {
    try {
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (mountedRef.current) setAal(data);
    } catch (e) {
      console.error('fetchAal error:', e);
      if (mountedRef.current) setAal(null);
    }
  };

  const handleSession = async (sessionUser: User | null) => {
    if (!mountedRef.current) return;
    setUser(sessionUser);
    // Sync Sentry user context
    setSentryUser(sessionUser ? { id: sessionUser.id, email: sessionUser.email } : null);
    if (sessionUser) {
      // Eager identify so events captured before the company/role lookups
      // resolve are still attributed to this user.
      identifyUser({ id: sessionUser.id });
      const [resolvedCompanyId, , role] = await Promise.all([
        fetchCompany(sessionUser.id),
        fetchAal(),
        fetchHighestRole(sessionUser.id),
      ]);
      // Re-identify with enriched cohort properties for tenant-level analytics.
      identifyUser({ id: sessionUser.id, companyId: resolvedCompanyId ?? undefined, role });
      // Fire-and-forget: populate session_fingerprints for sybil-defense layer.
      // Never blocks auth flow; server deduplicates repeat calls.
      void (async () => {
        try {
          const fp = await getBrowserFingerprint();
          const did = getOrCreateDeviceId();
          await supabase.functions.invoke("session-capture", {
            body: { browser_fingerprint: fp, device_id: did },
          });
        } catch (e) {
          Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
            level: "warning",
            tags: { source: "session-capture" },
          });
        }
      })();
      if (mountedRef.current) setLoading(false);
    } else {
      resetPostHog();
      setCompanyId(null);
      setAal(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    // 1. Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      // Skip if getSession already handled the initial state
      if (!initializedRef.current) return;
      // Use setTimeout to avoid Supabase deadlock with parallel auth calls
      setTimeout(() => handleSession(session?.user ?? null), 0);
    });

    // 2. Immediately check for existing session (with timeout fallback)
    const sessionTimeout = setTimeout(() => {
      if (!initializedRef.current && mountedRef.current) {
        console.warn('Supabase session check timed out after 8s');
        setConnectionError(true);
        setLoading(false);
      }
    }, 8000);

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      clearTimeout(sessionTimeout);
      initializedRef.current = true;
      if (mountedRef.current) setConnectionError(false);

      // Defensive: if we have a session token but the server rejects it
      // ("bad_jwt: missing sub claim" — happens when localStorage holds a
      // token from a different Supabase project / rotated signing key),
      // wipe it so the user gets a clean login state instead of an
      // infinite 403 loop on every authed request.
      if (session?.access_token) {
        try {
          const { error: userErr } = await supabase.auth.getUser();
          if (userErr) {
            const msg = (userErr.message || '').toLowerCase();
            if (msg.includes('jwt') || msg.includes('sub claim') || msg.includes('invalid')) {
              console.warn('[auth] stale/invalid JWT detected, signing out:', userErr.message);
              await supabase.auth.signOut().catch(() => {});
              try { localStorage.removeItem('sb-amjwnrglafnzwfrctqoh-auth-token'); } catch {}
              handleSession(null);
              return;
            }
          }
        } catch (e) {
          console.warn('[auth] getUser check failed (non-fatal):', e);
        }
      }

      handleSession(session?.user ?? null);
    }).catch((err: unknown) => {
      clearTimeout(sessionTimeout);
      console.error('getSession failed:', err);
      if (mountedRef.current) {
        setConnectionError(true);
        setLoading(false);
      }
    });

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    // Clear local state first so any in-flight queries that resolve mid-signout
    // don't repopulate the cache from the perspective of the previous user.
    if (mountedRef.current) {
      setUser(null);
      setCompanyId(null);
      setAal(null);
    }
    // Drop every cached query so a different user logging in on the same
    // browser tab does not see stale tenant data from the previous session.
    queryClient.clear();
    resetPostHog();
    setSentryUser(null);
    await supabase.auth.signOut();
  };

  const retryConnection = () => {
    setConnectionError(false);
    setLoading(true);
    initializedRef.current = false;
    supabase.auth.getSession().then(({ data: { session } }) => {
      initializedRef.current = true;
      if (mountedRef.current) setConnectionError(false);
      handleSession(session?.user ?? null);
    }).catch((err: unknown) => {
      console.error('Retry getSession failed:', err);
      if (mountedRef.current) {
        setConnectionError(true);
        setLoading(false);
      }
    });
  };

  return (
    <AuthContext.Provider value={{ user, loading, companyId, aal, connectionError, signOut, retryConnection }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
